import { IHeadersDataPagination } from "interfaces/pagination";
import { IColumnMeta } from "interfaces/table";
import { useEffect, useMemo, useState } from "react";
import { dateCreatedAtBodyTemplate, dateUpdatedAtBodyTemplate, statusActiveBodyTemplate } from "utils/bodyTemlates";
import { Box, Flex } from "@chakra-ui/react";
import Card from "components/card/Card";
import Table from "shared/Table";
import PaginatorPages from "shared/Paginator";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import AlertBox from "shared/AlertBox";
import TabBar from "shared/TabBar";
import ShortImage from "shared/ShortImage";
import { Link } from "react-router-dom";
import ButtonRounded from "shared/Button";
import { IItem } from "interfaces/itmes";
import DeleteMaterial from "./components/DeleteMaterial";

const Materials = () => {
  const [data, setData] = useState<IItem[]>([]);
  const [pagination, setPagination] = useState<IHeadersDataPagination>({
    paginationcurrent: 1,
    paginationlimit: 20,
    paginationtotal: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState(false);
  const { sharedData } = useAuth();
  const booksAPI = environments.domain + sharedData?.apisList?.WebSite?.Item?.General;

  const reFetch = async () => {
    await getData();
  };

  const getData = async () => {
    setLoading(true);
    try {
      const response: IApiMetaData = await callApi("GET", booksAPI, {}, pagination, {
        populatefields: "addedBy updatedBy subCategory",
        populateselected: "name",
      });

      if (response.Status === 200) {
        setPagination({
          ...pagination,
          paginationtotal: response.Pagination.total,
        });
        setData(response.Data.Content);
      } else {
        throw new Error(response.Message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error); // For debugging
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const convertPathImage = (path: string) => {
    if (!path) return "";
    const removeOldDomain = String(path).replace("http://datalib.cardiffinstitute.uk/", "");
    return `${environments.domain}/uploads/${removeOldDomain}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      await getData();
    };
    fetchData();
  }, [pagination.paginationcurrent, pagination.paginationlimit]);

  const columns: IColumnMeta<IItem>[] |any = useMemo(() => [
    {
      field: "title",
      header: "Title",
    },
    {
      field: "subCategory",
      header: "Category",
      body: (data: IItem) => data.subCategory?.name,
    },
    {
      field: "keywords",
      header: "Keywords",
      body: (data: IItem) => data.keywords.join(", "),
    },
    {
      field: "type",
      header: "Type",
    },
    {
      field: "cover",
      header: "Cover",
      body: (data: IItem) => <ShortImage src={convertPathImage(data.cover)} />,
    },
    {
      field: "isActive",
      header: "Status",
      body: statusActiveBodyTemplate,
    },
    {
      field: "addedBy",
      header: "Added By",
      body: (data: IItem) => data.addedBy?.name,
    },
    {
      field: "updatedBy",
      header: "Updated By",
      body: (data: IItem) => data.updatedBy?.name,
    },
    {
      field: "createdAt",
      header: "Created At",
      body: dateCreatedAtBodyTemplate,
    },
    {
      field: "updatedAt",
      header: "Updated At",
      body: dateUpdatedAtBodyTemplate,
    },
    {
      field: "edit",
      header: "Edit",
      body: (bookData: IItem) => (
        <Link to={"/admin/material/update"} state={bookData}>
          <ButtonRounded label="Edit" />
        </Link>
      ),
    },
    {
      field: "delete",
      header: "Delete",
      body: (data: IItem | any) => (
        <DeleteMaterial id={data._id} onSave={reFetch} />
      ),
    },
  ], [reFetch]);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card className="mb-4">
        <TabBar>
          <Link to="/admin/material/add">
            <ButtonRounded label="Add Book" />
          </Link>
        </TabBar>
      </Card>

      <Card>
        <Box mt="20px" mb="20px" width="100%">
          {!isError ? (
            <Box>
              <Table data={data} columns={columns} loading={loading} />
              <PaginatorPages
                pagination={pagination}
                setPagination={setPagination}
              />
            </Box>
          ) : (
            <Flex justifyContent="center" alignItems="center">
              <AlertBox
                status="error"
                description="Something went wrong. Please try again later."
                title="Error"
              />
            </Flex>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Materials;
